import React from "react";

interface Insight {
  title: string;
  meta: string;
  value: string;
  badge: {
    label: string;
    type: "blue" | "amber" | "green";
  };
  description: string;
}

export const CohortInsights: React.FC = () => {
  const insights: Insight[] = [
    {
      title: "Prime 2025 Q1 vs 2024 Q3",
      meta: "Default rate differential",
      value: "-0.9 pp",
      badge: {
        label: "Outperforming",
        type: "blue",
      },
      description:
        "2025 Q1 prime loans are tracking 90 bps lower cumulative defaults at 36M compared to 2024 Q3.",
    },
    {
      title: "High-LTV impact",
      meta: "LTV > 80% vs < 65%",
      value: "+1.7 pp",
      badge: {
        label: "Concentration",
        type: "amber",
      },
      description:
        "High-LTV segments show ~170 bps higher defaults at 48M, but account for only 23% of cohort balance.",
    },
    {
      title: "Yield vs credit cost",
      meta: "Net of losses",
      value: "+3.4%",
      badge: {
        label: "Attractive",
        type: "green",
      },
      description:
        "After credit losses, selected cohorts still deliver ~340 bps excess yield vs 5-yr GoC benchmark.",
    },
  ];

  const getBadgeStyles = (type: string) => {
    switch (type) {
      case "blue":
        return "border-[rgba(0,212,255,0.8)] text-cyber-blue bg-[rgba(0,212,255,0.1)]";
      case "amber":
        return "border-[rgba(255,184,0,0.8)] text-warning-amber bg-[rgba(255,184,0,0.1)]";
      case "green":
        return "border-[rgba(0,255,136,0.8)] text-neon-green bg-[rgba(0,255,136,0.12)]";
      default:
        return "border-[rgba(0,212,255,0.8)] text-cyber-blue bg-[rgba(0,212,255,0.1)]";
    }
  };

  return (
    <article className="relative p-4 pb-4.5 rounded-[18px] bg-gradient-to-br from-[#22284d] to-[#10142f] border border-[rgba(139,146,176,0.2)] shadow-[0_16px_40px_rgba(0,0,0,0.6)] overflow-hidden">
      <div className="flex items-center justify-between gap-2 mb-2.5">
        <div className="flex flex-col gap-0.5">
          <div className="text-[15px] font-medium tracking-[0.06em] uppercase">
            Cohort insights
          </div>
          <div className="text-[11px] text-[#8b92b0]">
            High-level takeaways for the selected filters and vintages.
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-2.5">
        {insights.map((insight, index) => (
          <div
            key={index}
            className="p-3.5 rounded-xl bg-[rgba(5,7,22,0.95)] border border-[rgba(139,146,176,0.5)] text-xs flex flex-col gap-1.5"
          >
            <div className="flex items-center justify-between gap-1.5">
              <div>
                <div className="text-xs font-medium">{insight.title}</div>
                <div className="text-[11px] text-[#8b92b0]">{insight.meta}</div>
              </div>
              <span
                className={`rounded-full px-2.5 py-1 text-[10px] border ${getBadgeStyles(
                  insight.badge.type
                )}`}
              >
                {insight.badge.label}
              </span>
            </div>
            <div className="font-jetbrains-mono text-[17px]">
              {insight.value}
            </div>
            <div className="text-[11px] text-[#8b92b0]">
              {insight.description.split(/\*\*/).map((part, i) =>
                i % 2 === 1 ? (
                  <strong key={i} className="text-white">
                    {part}
                  </strong>
                ) : (
                  part
                )
              )}
            </div>
          </div>
        ))}
      </div>
    </article>
  );
};
